import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { RM } from '../lib/rm';
import type { Attachment } from '../lib/attachments';
import { TwinMessage, UserMessage } from './Message';

export type ChatMessage = {
  id: string;
  role: string;
  content: string;
  experimental_attachments?: Attachment[];
};

type Props = {
  messages: ChatMessage[];
  busy: boolean;
  searching?: boolean;
  restoredCount?: number;
  error?: string;
  flipRectRef: React.MutableRefObject<DOMRect | null>;
  onLanded?: () => void;
};

export function MessageStream({ messages, busy, searching, restoredCount = 0, error, flipRectRef, onLanded }: Props) {
  const streamRef = useRef<HTMLDivElement>(null);

  // Keep the newest content in view.
  useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy, error]);

  // Push all but the last two messages onto a receded depth plane (once).
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    const nodes = [...el.querySelectorAll<HTMLElement>('.msg')];
    nodes.forEach((m, i) => {
      const back = i < nodes.length - 2;
      if (back && !m.dataset.back) {
        m.dataset.back = '1';
        if (RM) {
          gsap.set(m, { opacity: 0.55, scale: 0.985 });
        } else {
          gsap.to(m, { opacity: 0.55, scale: 0.985, transformOrigin: 'left center', duration: 0.5, ease: 'power2.out' });
        }
      }
    });
  }, [messages, busy, error]);

  const last = messages[messages.length - 1];
  const showPending = busy && (!last || last.role === 'user');

  return (
    <div className="stream" ref={streamRef}>
      {messages.map((m, i) => {
        if (m.role === 'user') {
          return (
            <UserMessage
              key={m.id}
              content={m.content}
              attachments={m.experimental_attachments}
              flipRect={flipRectRef.current}
            />
          );
        }
        const isLast = i === messages.length - 1;
        return (
          <TwinMessage
            key={m.id}
            content={m.content}
            streaming={isLast && busy}
            instant={i < restoredCount}
            onLanded={isLast ? onLanded : undefined}
          />
        );
      })}
      {showPending && <TwinMessage key="pending" content="" streaming searching={searching} />}
      {error && (
        <div className="msg twin">
          <div className="bubble err">{error}</div>
        </div>
      )}
    </div>
  );
}
